import React from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import Tooltip from "antd/lib/tooltip";
import { Message as IMessage } from "Models/message";
import { GRAY, BACKGROUND } from "Components/constants";
import { TICK } from "Models/constants";
import { Position } from "./types";
import { dispatch } from "Redux/store";
import { getFocusedMessage, getPace } from "Redux/selectors";
import { setGlobal, updateLogMessage } from "Redux/actions";
import styles from "./Message.module.css";

type MessageProps = {
    color?: number;
    replica?: boolean;
    message: IMessage;
    visible?: boolean;
};

const Message = ({
    message,
    color = GRAY,
    replica = false,
    visible = true,
}: MessageProps) => {
    const pace = useSelector(getPace);
    const ref = React.useRef<HTMLDivElement>(null);
    const [translate, setTranslate] = React.useState(true);
    const [originOffset, setOriginOffset] = React.useState<Position>();

    const focusedMessage = useSelector(getFocusedMessage);

    const handleClick = React.useCallback(() => {
        if (focusedMessage === message.id) {
            dispatch(setGlobal({ focusedMessage: undefined }));
            return;
        }

        dispatch(setGlobal({ focusedMessage: message.id }));
    }, [message.id, focusedMessage]);

    React.useEffect(() => {
        if (message.originUid === null) return;

        const timeout = setTimeout(() => {
            dispatch(
                updateLogMessage(message.uid, {
                    ...message,
                    originUid: null,
                })
            );
        }, pace * TICK * 0.25);

        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message]);

    React.useEffect(() => {
        if (!ref.current) return;

        const originElement =
            message.originUid && document.getElementById(message.originUid);

        if (!originElement) {
            setOriginOffset([0, 0]);
            return;
        }

        const originRect = originElement.getBoundingClientRect();
        const messageRect = ref.current.getBoundingClientRect();

        const newOriginOffset: Position = [
            originRect.x - messageRect.x,
            originRect.y - messageRect.y,
        ];

        setOriginOffset(newOriginOffset);
    }, [message.originUid]);

    React.useEffect(() => {
        setTranslate(true);
        let raf = requestAnimationFrame(() => {
            raf = requestAnimationFrame(() => {
                setTranslate(false);
            });
        });

        return () => cancelAnimationFrame(raf);
    }, [originOffset]);

    return (
        <Tooltip placement="top" title="Message">
            <div
                className={clsx(styles.root, {
                    [styles.animate]: !translate,
                    [styles.fadeIn]: !originOffset,
                    [styles.invisible]: !visible,
                    [styles.focused]: focusedMessage === message.id,
                })}
                style={
                    translate && originOffset
                        ? {
                              transform: `translate(${originOffset[0]}px,${originOffset[1]}px)`,
                          }
                        : {}
                }
                id={message.uid}
                ref={ref}
                onClick={handleClick}
            >
                <div
                    className={styles.box}
                    style={{
                        backgroundColor: `#${BACKGROUND?.toString(16)}`,
                        borderColor: `#${color?.toString(16)}`,
                    }}
                >
                    <div
                        className={clsx(styles.background, {
                            [styles.replica]: replica,
                        })}
                        style={{
                            backgroundColor: `#${color?.toString(16)}`,
                        }}
                    />
                    <div
                        className={styles.label}
                        style={{
                            color: replica
                                ? `#${color?.toString(16)}`
                                : "white",
                        }}
                    >
                        {message.value}
                    </div>
                </div>
            </div>
        </Tooltip>
    );
};

export default Message;
